import React from 'react';

import cs from 'classnames';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';

import { ia } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import NotFound from 'components/practice/Dashboard/components/NotFound';

import TagPill from '../../TagPill/TagPill';

const Reorder = ({
  importantTags = [],
  regularTags = [],
  onDragEnd = () => {},
  handleRemove = () => {},
  fromPatientChart = false
}) => {
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    onDragEnd(result);
  };

  return (
    <>
      {ia(importantTags) || ia(regularTags) ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex max-h-[240px] flex-col gap-3 overflow-y-auto">
            <div className="flex flex-col gap-1">
              <p className="text-sm text-neutral-800">Important tags</p>
              <Droppable droppableId="importantTags">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cs(
                      'flex min-h-[40px] flex-col gap-2 rounded-md py-2',
                      snapshot.isDraggingOver && 'bg-primary-50',
                      !importantTags.length &&
                        'items-center justify-center border border-dashed border-neutral-300'
                    )}>
                    {importantTags.length > 0 ? (
                      importantTags?.map((tag, index) => (
                        <Draggable key={tag?.id} draggableId={String(tag?.id)} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? 0.8 : 1
                              }}
                              className="flex items-center gap-1 truncate">
                              <div
                                {...provided.dragHandleProps}
                                className="cursor-grab p-1 active:cursor-grabbing">
                                <Icon
                                  icon="drag-drop-linear"
                                  className="cursor-grab active:cursor-grabbing"
                                />
                              </div>
                              <TagPill
                                tag={tag}
                                className="max-h-[25px] max-w-full"
                                onRemove={
                                  fromPatientChart
                                    ? (removedTag) =>
                                        handleRemove({
                                          tag: removedTag,
                                          isSelected: true,
                                          fromView: true
                                        })
                                    : undefined
                                }
                              />
                            </div>
                          )}
                        </Draggable>
                      ))
                    ) : (
                      <span className="text-sm text-neutral-500">
                        Drag tags here to mark as important
                      </span>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>

            <div className="flex flex-col gap-1">
              <p className="text-sm text-neutral-800">
                {ia(importantTags) ? 'All other tags on this chart' : 'All tags on this chart'}
              </p>
              <Droppable droppableId="regularTags">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cs(
                      'flex flex-col rounded-md py-2',
                      snapshot.isDraggingOver && 'bg-primary-50',
                      !regularTags.length &&
                        'items-center justify-center border border-dashed border-neutral-300'
                    )}>
                    {regularTags.length > 0 ? (
                      regularTags?.map((tag, index) => (
                        <Draggable key={tag?.id} draggableId={String(tag?.id)} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? 0.8 : 1
                              }}
                              className="mt-2 flex items-center gap-1 truncate first-of-type:mt-0">
                              <div
                                {...provided.dragHandleProps}
                                className="cursor-grab p-1 active:cursor-grabbing">
                                <Icon
                                  icon="drag-drop-linear"
                                  className="cursor-grab active:cursor-grabbing"
                                />
                              </div>
                              <TagPill
                                tag={tag}
                                className="max-h-[25px] max-w-full"
                                onRemove={
                                  fromPatientChart
                                    ? (removedTag) =>
                                        handleRemove({
                                          tag: removedTag,
                                          isSelected: true,
                                          fromView: true
                                        })
                                    : undefined
                                }
                              />
                            </div>
                          )}
                        </Draggable>
                      ))
                    ) : (
                      <span className="text-sm text-neutral-500">
                        Drag tags here to mark as regular
                      </span>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>
        </DragDropContext>
      ) : (
        <NotFound
          icon="illustration-empty-cpt-codes"
          title="No tags for this patient"
          description="Add new tags by clicking the add tags button"
          titleClassName="text-md"
        />
      )}
    </>
  );
};

export default withErrorBoundary(Reorder);
